/**
 * Hobbies component - Displays personal interests and life quotes
 * Uses animation on scroll with intersection observer
 */
import React, { useEffect, useState, useRef } from "react";
import { useInView } from 'react-intersection-observer';
import { useLanguage } from "../../../../hooks";
import * as styles from "./hobbies.css.ts";
import { commonClasses } from "../../../../styles/indexStyles";
import LifeQuote from '../LifeQuote/LifeQuote';
import { HobbyGrid } from './HobbyGrid/HobbyGrid';

export const Hobbies: React.FC = () => {
  // ================================
  // HOOKS ET ÉTAT
  // ================================
  
  // Animation state management
  const [isVisible, setIsVisible] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  
  // ✅ CORRECTION : Utilisation de useLanguage pour les traductions
  const { t } = useLanguage();
  
  // References for child components animation
  const quoteRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // ================================
  // EFFECTS
  // ================================
  
  // Update visibility when section comes into view
  useEffect(() => {
    if (inView) {
      setIsVisible(true);
    }
  }, [inView]);

  // ================================
  // STYLES D'ANIMATION
  // ================================
  
  // Animation styles with fade-in and slide-up effect
  const animationStyle: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
  };

  // ================================
  // RENDU PRINCIPAL
  // ================================
  
  return (
    <section 
      id="hobbies" 
      className={styles.section} 
      ref={ref}
      aria-labelledby="hobbies-main-title"
    >
      <div className={commonClasses.container} style={animationStyle}>
        
        {/* ================================ */}
        {/* TITRE DE SECTION */}
        {/* ================================ */}
        
        <h2 
          id="hobbies-main-title" 
          className={`${commonClasses.title} ${styles.title}`}
        >
          <span className={`${commonClasses.highlight} ${styles.highlight}`} aria-hidden="true">07.</span>
          {t('hobbies.title')}
        </h2>
        
        {/* ================================ */}
        {/* CONTENU PRINCIPAL */}
        {/* ================================ */}
        
        <div className={styles.content}>
          
          {/* Section des citations inspirantes */}
          <div className={styles.quoteSection} ref={quoteRef}>
            <h3 className={styles.quoteTitle} id="quote-section-title">
              Citations inspirantes
            </h3>
            <LifeQuote />
          </div>
          
          {/* Grille des hobbies */}
          <div ref={gridRef}>
            <HobbyGrid />
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default Hobbies;

